import { isValidElement, ReactElement, useCallback, useId } from 'react';
import { css, Theme } from '@emotion/react';
import MuiMenuList from '@mui/material/MenuList';
import { MenuItem, MenuItemProps } from './MenuItem';

export type MenuItemType = MenuItemProps | ReactElement<{ id: string }>;

export interface MenuListProps {
  items: MenuItemType[];

  small?: boolean;

  /**
   * Export just the menu items without the list wrapper.
   * Is used internally by the {@link Menu} component to display items.
   */
  excludeListComponent?: boolean;

  onSelect?: (id: string) => void;
}

export const MenuList = ({ items, small, excludeListComponent = false, onSelect, ...other }: MenuListProps) => {
  const id = useId();

  const renderItem = useCallback(
    (item: MenuItemType) => {
      if (isValidElement(item)) {
        const Element = item as ReactElement<{ id: string }>;
        return <Element.type key={Element.props.id} {...Element.props} />;
      }

      return (
        <MenuItem
          key={item.id}
          small={small}
          {...item}
          id={`${id}.${item.id}`}
          onSelect={(id: string) => {
            if (onSelect) onSelect(item.id);
            if (item.onSelect) item.onSelect(item.id);
          }}
        />
      );
    },
    [id, onSelect]
  );

  if (excludeListComponent) return <>{items.map(renderItem)}</>;

  return (
    <MuiMenuList disablePadding css={menuListStyle} {...other}>
      {items.map(renderItem)}
    </MuiMenuList>
  );
};

const menuListStyle = (theme: Theme) => css`
  padding: 0.5rem;
  background-color: ${theme.color.background};

  .MuiMenuItem-root {
    &:not(:first-of-type):not(.link) {
      margin-top: 0.25rem;
    }
  }

  > .divider {
    margin: 1.4rem 0;

    svg {
      display: none;
    }
  }
`;
